import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Box from '@mui/material/Box';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { api } from '../../config';
import ClearIcon from '@mui/icons-material/Clear';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { toast } from 'react-toastify';

const FinanzaModal = ({
                          isOpen,
                          onClose,
                          modalType,
                          allProjects,
                          currentSelections = [],
                          onSelectionsUpdate
                      }) => {
    const [selectedProjects, setSelectedProjects] = useState([...currentSelections]);
    const [showMaxAlert, setShowMaxAlert] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredProjects, setFilteredProjects] = useState(allProjects);

    // Imposta il numero massimo di selezioni in base al modalType
    const getMaxSelections = () => {
        switch(modalType) {
            case "carousel": return 5;
            case "miniCarousel": return 9;
            case "header": return 0;
            default: return 5;
        }
    };

    const maxSelections = getMaxSelections();

    // Sincronizza lo stato quando cambiano le selezioni correnti
    useEffect(() => {
        setSelectedProjects([...currentSelections]);
    }, [currentSelections, isOpen]);

    // Filtra i progetti in base alla ricerca
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredProjects(allProjects);
        } else {
            const filtered = allProjects.filter(project =>
                project.titolo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                project.citta?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredProjects(filtered);
        }
    }, [searchTerm, allProjects]);

    const handleProjectSelect = (project) => {
        if (selectedProjects.find(p => p.id === project.id)) {
            // Deseleziona se già selezionato
            setSelectedProjects(selectedProjects.filter(p => p.id !== project.id));
            setShowMaxAlert(false);
        } else {
            // Seleziona se non supera il massimo
            if (selectedProjects.length < maxSelections) {
                setSelectedProjects([...selectedProjects, project]);
                setShowMaxAlert(false);
            } else {
                // Mostra alert se si cerca di selezionare oltre il massimo
                setShowMaxAlert(true);
                setTimeout(() => setShowMaxAlert(false), 3000);
            }
        }
    };

    const isProjectSelected = (project) => {
        return selectedProjects.some(p => p.id === project.id);
    };

    const handleSave = async () => {
        try {
            // Mostra toast di caricamento
            const loadingToast = toast.loading("Salvataggio in corso...");

            // Salva nel database
            const response = await fetch(api('/saveFinanzaSelection.php'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sectionType: modalType,
                    selectedProjects: selectedProjects
                })
            });

            if (!response.ok) {
                throw new Error(`Errore HTTP: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                // Salva anche nel localStorage come fallback
                const storageKey = `finanza_${modalType}_selections`;
                const projectIds = selectedProjects.map(p => p.id);
                localStorage.setItem(storageKey, JSON.stringify(projectIds));

                // Aggiorna il toast con successo
                toast.update(loadingToast, {
                    render: `✅ ${result.message}`,
                    type: "success",
                    isLoading: false,
                    autoClose: 2000
                });

                console.log('Salvataggio riuscito:', result.message);

                // Aspetta che il toast si chiuda e poi aggiorna la pagina
                setTimeout(() => {
                    onSelectionsUpdate(modalType, selectedProjects);
                    window.location.reload(); // Aggiorna la pagina automaticamente
                }, 2200);

            } else {
                // Aggiorna il toast con errore
                toast.update(loadingToast, {
                    render: `❌ Errore: ${result.message}`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000
                });
                console.error('Errore nel salvataggio:', result.message);
            }
        } catch (error) {
            console.error('Errore di rete:', error);
            toast.error(`Errore di rete: ${error.message}`);
        }
    };

    const getModalTitle = () => {
        switch(modalType) {
            case "carousel": return "Seleziona Progetti per Carosello Principale (max 5)";
            case "miniCarousel": return "Seleziona Progetti per Galleria (max 9)";
            case "header": return "Modifica Testo Header";
            default: return "Modifica Sezione";
        }
    };

    const getProjectImage = (project) => {
        if (Array.isArray(project.images) && project.images.length > 0) {
            const heroImage = project.images.find(img => img.isHero) || project.images[0];
            const path = heroImage?.path || "";
            if (!path) return '/placeholder-image.jpg';

            if (path.includes(',')) {
                return api(`/Progetti/${path}`);
            }
            return api(`/Progetti/${project.id}/${path}`);
        }

        if (project.heroImage) {
            const path = project.heroImage;
            if (path.includes(',')) {
                return api(`/Progetti/${path}`);
            }
            return api(`/Progetti/${project.id}/${path}`);
        }

        return '/placeholder-image.jpg';
    };

    // Funzione per caricare le selezioni salvate dal localStorage
    const loadSavedSelections = () => {
        const storageKey = `finanza_${modalType}_selections`;
        const savedIds = localStorage.getItem(storageKey);
        if (savedIds) {
            const ids = JSON.parse(savedIds);
            const savedProjects = allProjects.filter(project =>
                ids.includes(project.id)
            );
            setSelectedProjects(savedProjects);
        }
    };

    // Carica le selezioni salvate quando il modal si apre
    useEffect(() => {
        if (isOpen) {
            loadSavedSelections();
            setSearchTerm(""); // Reset della ricerca quando si apre il modal
        }
    }, [isOpen, modalType]);

    const clearSearch = () => {
        setSearchTerm("");
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            scroll="paper"
            sx={{
                '& .MuiDialog-paper': {
                    minHeight: '80vh',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: "linear-gradient(90deg, #99191b 0%, #e3764c 100%)",
                color: 'white',
                py: 2
            }}>
                <Box>
                    <Typography variant="h6" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
                        {getModalTitle()}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                        {allProjects.length} progetti disponibili - {maxSelections} selezionabili
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>
                {modalType === "header" ? (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Modifica testo header - da implementare
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ width: '100%' }}>
                        {/* BARRA DI RICERCA */}
                        <Box sx={{ mb: 3 }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Cerca progetti per titolo o città..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: 'text.secondary' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={clearSearch}
                                                edge="end"
                                                size="small"
                                            >
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />

                            {/* Risultati ricerca */}
                            {searchTerm && (
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                    {filteredProjects.length} progetti trovati per "{searchTerm}"
                                </Typography>
                            )}
                        </Box>

                        {/* Contatore e Alert */}
                        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body1" sx={{
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 500,
                                color: selectedProjects.length === maxSelections ? '#99191b' : 'inherit'
                            }}>
                                Progetti selezionati: {selectedProjects.length} / {maxSelections}
                            </Typography>

                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Visualizzati: {filteredProjects.length} / {allProjects.length}
                            </Typography>
                        </Box>

                        {showMaxAlert && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                È possibile selezionare massimo {maxSelections} progetti.
                                Deseleziona un progetto per poterne selezionare un altro.
                            </Alert>
                        )}

                        {/* Griglia progetti */}
                        <ImageList
                            cols={4}
                            gap={16}
                            sx={{
                                width: '100%',
                                margin: 0,
                                '@media (max-width: 1200px)': {
                                    cols: 3,
                                },
                                '@media (max-width: 768px)': {
                                    cols: 2,
                                },
                                '@media (max-width: 480px)': {
                                    cols: 1,
                                }
                            }}
                        >
                            {filteredProjects.map((project) => (
                                <ImageListItem
                                    key={project.id}
                                    onClick={() => handleProjectSelect(project)}
                                    sx={{
                                        cursor: selectedProjects.length >= maxSelections && !isProjectSelected(project)
                                            ? 'not-allowed'
                                            : 'pointer',
                                        border: isProjectSelected(project) ?
                                            '4px solid #99191b' : '2px solid #e0e0e0',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        transition: 'all 0.3s ease',
                                        transform: isProjectSelected(project) ? 'scale(0.98)' : 'scale(1)',
                                        opacity: selectedProjects.length >= maxSelections && !isProjectSelected(project)
                                            ? 0.6
                                            : 1,
                                        '&:hover': {
                                            transform: isProjectSelected(project) ?
                                                'scale(0.98)' :
                                                (selectedProjects.length >= maxSelections && !isProjectSelected(project)
                                                    ? 'scale(1)'
                                                    : 'scale(1.02)'),
                                            borderColor: isProjectSelected(project) ?
                                                '#99191b' :
                                                (selectedProjects.length >= maxSelections && !isProjectSelected(project)
                                                    ? '#e0e0e0'
                                                    : '#bdbdbd'),
                                        },
                                        position: 'relative'
                                    }}
                                >
                                    {/* Indicatore selezione */}
                                    {isProjectSelected(project) && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                backgroundColor: '#99191b',
                                                color: 'white',
                                                borderRadius: '50%',
                                                width: 28,
                                                height: 28,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                zIndex: 2,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                            }}
                                        >
                                            ✓
                                        </Box>
                                    )}

                                    {/* Numero ordine per progetti selezionati */}
                                    {isProjectSelected(project) && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                left: 8,
                                                backgroundColor: 'rgba(0,0,0,0.7)',
                                                color: 'white',
                                                borderRadius: '4px',
                                                padding: '2px 6px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                zIndex: 2
                                            }}
                                        >
                                            #{selectedProjects.findIndex(p => p.id === project.id) + 1}
                                        </Box>
                                    )}

                                    <img
                                        srcSet={`${getProjectImage(project)}?w=248&fit=crop&auto=format&dpr=2 2x`}
                                        src={`${getProjectImage(project)}?w=248&fit=crop&auto=format`}
                                        alt={project.titolo}
                                        loading="lazy"
                                        style={{
                                            width: '100%',
                                            height: '180px',
                                            objectFit: 'cover',
                                            display: 'block'
                                        }}
                                    />
                                    <Box sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        background: 'linear-gradient(transparent, rgba(0,0,0,0.9))',
                                        color: 'white',
                                        padding: '12px'
                                    }}>
                                        <Typography sx={{
                                            margin: 0,
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            fontFamily: 'Poppins, sans-serif',
                                            lineHeight: 1.2
                                        }}>
                                            {project.titolo || 'Senza titolo'}
                                        </Typography>
                                        <Typography sx={{
                                            margin: 0,
                                            fontSize: '12px',
                                            fontFamily: 'Poppins, sans-serif',
                                            opacity: 0.9,
                                            mt: 0.5
                                        }}>
                                            {project.citta || 'Nessuna città'}
                                        </Typography>
                                    </Box>

                                    {/* Overlay per progetti non selezionabili */}
                                    {selectedProjects.length >= maxSelections && !isProjectSelected(project) && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(0,0,0,0.4)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                zIndex: 1
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: 'white',
                                                    fontWeight: 'bold',
                                                    textAlign: 'center',
                                                    p: 2
                                                }}
                                            >
                                                Limite massimo raggiunto
                                            </Typography>
                                        </Box>
                                    )}
                                </ImageListItem>
                            ))}
                        </ImageList>

                        {/* Messaggio se nessun progetto trovato */}
                        {filteredProjects.length === 0 && searchTerm && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                                    Nessun progetto trovato
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Prova a modificare i termini di ricerca
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button
                    onClick={() => {
                        setSelectedProjects([...currentSelections]);
                        onClose();
                    }}
                    variant="outlined"
                    sx={{
                        borderColor: '#99191b',
                        color: '#99191b',
                        '&:hover': {
                            borderColor: '#99191b',
                            backgroundColor: 'rgba(153, 25, 27, 0.04)'
                        }
                    }}
                >
                    Annulla
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={selectedProjects.length === 0}
                    sx={{
                        background: "linear-gradient(90deg, #99191b 0%, #e3764c 100%)",
                        '&:hover': {
                            background: "#99191b",
                            transform: 'translateY(-1px)'
                        },
                        '&:disabled': {
                            background: '#cccccc',
                            color: '#666666'
                        },
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 500
                    }}
                >
                    Salva {selectedProjects.length} Progetti
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FinanzaModal;